<template>
  <div :class="$style.container">
    <ItemTable
      v-bind="itemTableProps"
      defaultSortField="lastname"
      :disableSearchBar="true"
      :disableSelectAllItems="tableImportConditions.disableSelectAllUsers"
      :disableSelectItems="tableImportConditions.disableSelectUsers"
    >
      <template v-slot:customBar>
        <div
          :class="$style.checkboxContainer"
          @change="onFilterCheckboxChange"
        >
          <CheckboxWithLabel
            :classNames="classStyle"
            label="Show NEW users only"
            name="isNew"
            :preventDefault="true"
            :value="false"
          />
        </div>
      </template>
    </ItemTable>
    <LoadingOverlay
      :showLoadingOverlay="showLoadingOverlay"
      :zIndex="Number(itemTableZIndex) + 1"
    />
  </div>
</template>

<script>
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { itemTableZIndex } from 'client/css/z-index.css';
import { mapActions } from 'vuex';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel.vue';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import LoadingOverlay from 'client/components/LoadingOverlay/LoadingOverlay.vue';

const UserStatusFilters = [
  {
    key: 'isNew',
    value: true,
  },
];

const storeModule = StoreModuleMap[ItemTypeConsts.scimUserImport];

// Columns to pass to ItemTable
const scimUsersImportTableColumns = [
  {
    bodyComponent: 'ImportStatus',
    bodyComponentProps: {
      field: 'isNew',
    },
    classNames: {
      body: ['importStatusColumn'],
    },
    headingText: 'Import status',
  },
  {
    bodyComponent: 'EditableInput',
    bodyComponentProps: {
      field: 'lastname',
      hideErrorValidation: true,
      showReadOnly: true,
      storeModule,
      type: 'text',
    },
    headingText: 'Last Name',
  },
  {
    bodyComponent: 'EditableInput',
    bodyComponentProps: {
      field: 'firstname',
      hideErrorValidation: true,
      showReadOnly: true,
      storeModule,
      type: 'text',
    },
    headingText: 'First Name',
  },
  {
    bodyComponent: 'EditableInput',
    bodyComponentProps: {
      field: 'email',
      hideErrorValidation: true,
      showReadOnly: true,
      storeModule,
      type: 'email',
      validation: 'required|email',
    },
    headingText: 'Email',
  },
  {
    bodyComponent: 'EditableInput',
    bodyComponentProps: {
      field: 'creationSource',
      hideErrorValidation: true,
      showReadOnly: true,
      storeModule,
      type: 'text',
    },
    headingText: 'Creation source',
  },
];

export default {
  name: 'ScimUsersImportSelection',

  components: {
    CheckboxWithLabel,
    ItemTable,
    LoadingOverlay,
  },

  props: {
    appConnectionType: {
      type: String,
      required: true,
    },
    scimAppId: {
      type: String,
      required: true,
    },
    scimAppSourceName: {
      type: String,
      required: true,
    },
    tableImportConditions: {
      default: () => ({}),
      type: Object,
    },
  },

  data() {
    return {
      classStyle: {
        label: [this.$style.labelText],
        input: '',
      },
      showLoadingOverlay: false,
      UserStatusFilters,
      itemTableZIndex,
      itemTableProps: {
        columns: scimUsersImportTableColumns,
        legacyRowStyles: false,
        details: false,
        inPanel: false,
        itemType: ItemTypeConsts.scimUserImport,
        selectionItemText: {
          label: 'selected',
          plural: 'users',
          singular: 'user',
        },
        hasSelectColumn: true,
        hideSelectionTotalCount: true,
        showActionBar: true,
        showBound: false,
        showSearchForm: true,
        useLargeTableCells: true,
      },
    };
  },

  created() {
    this.setScimApplicationId({ scimAppId: this.$props.scimAppId });
    this.setUsersFilterField({ key: '', value: '' }); // Clear filter
    this.setScimApplicationSourceName({ scimAppSourceName: this.$props.scimAppSourceName });
    this.setApplicationConnectionType({ appConnectionType: this.$props.appConnectionType });
  },

  methods: {
    ...mapActions(storeModule, [
      'setApplicationConnectionType',
      'setScimApplicationId',
      'setScimApplicationSourceName',
      'setUsersFilterField',
      'resetCollection',
      'fetchCollection',
      'setLoadingOverlay',
    ]),

    /**
   * Run all necessary ItemTable methods to update the user import Modal ItemTable
   */
    executeFetch() {
      this.resetCollection(true);
      this.showLoadingOverlay = true;
      this.fetchCollection().then(() => { this.showLoadingOverlay = false; });
    },
    /**
     * Set filter key-value pair on the collection store module
     *
     * The event comes from a **bubbled-up** event from the
     * CheckboxWithLabel component. We extract the checkbox boolean value
     * and also the name to use as key for the filter attribute.
     */
    onFilterCheckboxChange(event) {
      const { name, checked } = event.target;
      let key = '';
      let value = '';

      if (checked) { // Filter checkbox toggled on
        const filter = UserStatusFilters.find((filterKeyPair) => filterKeyPair.key === name);
        key = filter.key;
        value = filter.value;
      }

      this.setUsersFilterField({ key, value }); // Set filter on storeModule (ScimUsersCollection)

      this.executeFetch();
    },
  },
};
</script>

<style module>
@value (jcSecondary, jcGrey400) from '~css/brand-colors.css';

.checkboxContainer {
  display: flex;
  margin-right: 15px;
}

.container {
  height: 100%;
}

.labelText {
  color: jcSecondary;
  font-size: 14px !important;
}

.loadingContainer {
  align-items: center;
  display: flex;
  height: 50%;
  justify-content: center;
  width: 50%;
}

.loadingIcon {
  fill: jcGrey400;
  height: 30%;
  width: 30%;
}
</style>
